/* Store 01 — Free Gift automation
   ------------------------------------------------------------------
   Keeps up to TWO independent free gifts in the cart:
     - "every"  — unlocked once the cart has at least one qualifying item.
     - "min"    — unlocked once qualifying items' subtotal reaches a
                  merchant-configured minimum (settings.free_gift_min_amount).
   Each promo adds/removes/quantity-corrects its own gift line, and each
   gift line is tagged with a hidden line-item property (_free_gift) whose
   value is the promo key ("every"/"min") — never "yes" — so a customer
   buying a gift product on its own is never touched, and each promo is
   independently distinguishable on the cart line.

   Config is injected by snippets/s01-free-gift.liquid as
   window.S01FreeGift.promos = { every?: {...}, min?: {...} }, gated by the
   `free_gift_enabled` / `free_gift_min_enabled` theme settings.

   When a promo newly unlocks, a celebration modal (<s01-gift-modal>, see
   snippets/s01-gift-modal.liquid) pops listing the gift(s) and flies a
   thumbnail of each toward the header cart icon.

   NOTE: this controls presence/quantity only. For a gift to actually cost
   nothing, its product/variant must be priced 0 (or covered by an
   automatic discount) in the store's admin. */
(function () {
  const cfg = window.S01FreeGift;
  if (!cfg || !cfg.promos) return;

  const ALL_PROMOS = Object.keys(cfg.promos)
    .map((key) => cfg.promos[key])
    .filter((p) => p && p.variantId);
  const PROMOS = ALL_PROMOS.filter((p) => p.enabled);
  if (!ALL_PROMOS.length) return;

  const GIFT_PROP = '_free_gift';
  const SOURCE = 's01-free-gift';
  const R = window.routes || {};
  const CART_URL = R.cart_url || '/cart';
  const ADD_URL = (R.cart_add_url || '/cart/add') + '.js';
  const CHANGE_URL = (R.cart_change_url || '/cart/change') + '.js';

  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  let queue = Promise.resolve();
  let lastCart = null;

  function promoOf(it) {
    const key = it.properties && it.properties[GIFT_PROP];
    if (!key) return null;
    return ALL_PROMOS.find((p) => p.key === key && Number(it.variant_id) === Number(p.variantId)) || null;
  }
  function isGiftLine(it) {
    return !!promoOf(it);
  }
  function giftLineFor(cart, promo) {
    return cart.items.find((it) => promoOf(it) === promo) || null;
  }

  function getCart() {
    return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }
  function addGift(promo) {
    return fetch(ADD_URL, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: Number(promo.variantId), quantity: 1, properties: { [GIFT_PROP]: promo.key } }] }),
    }).then((r) => r.json());
  }
  // Target the specific line by its key so a paid line of the same variant is safe.
  function setGiftQty(key, quantity) {
    return fetch(CHANGE_URL, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json());
  }

  function qualifyingItems(cart) {
    return cart.items.filter((it) => !isGiftLine(it));
  }
  function qualifyingCount(cart) {
    return qualifyingItems(cart).reduce((n, it) => n + it.quantity, 0);
  }
  function qualifyingSubtotalCents(cart) {
    return qualifyingItems(cart).reduce((n, it) => n + it.final_line_price, 0);
  }
  function promoUnlocked(cart, promo) {
    if (!promo.enabled) return false;
    if (promo.key === 'min') return qualifyingSubtotalCents(cart) >= Number(promo.minAmountCents || 0);
    return qualifyingCount(cart) > 0;
  }

  /* ---- Full cart-UI refresh ------------------------------------------------
     Dawn's external cart-update handler only swaps the line-items HTML; it does
     NOT refresh the footer, the header count, or the empty-state class. So when
     WE mutate the cart we re-render everything ourselves, mirroring what Dawn
     does on its own quantity changes. This is what makes the cart page reach its
     proper empty state (and the header bubble clear) without a manual reload. */
  function getSections(ids) {
    return fetch(CART_URL + '?sections=' + encodeURIComponent(ids.join(',')), {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());
  }
  function sectionInnerHTML(html, selector) {
    const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return found ? found.innerHTML : '';
  }
  function updateCountBubbles(cart) {
    const count = cart.item_count;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('hidden', count === 0);
    });
  }
  function cartSectionList() {
    const list = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) list.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer && footer.dataset.id) list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    return list;
  }
  function refreshUI(cart) {
    updateCountBubbles(cart);
    const list = cartSectionList();
    if (!list.length) return Promise.resolve(); // not on the cart page — bubble was enough
    return getSections(list.map((s) => s.section))
      .then((secs) => {
        const empty = cart.item_count === 0;
        const wrapper = document.querySelector('cart-items');
        const footer = document.getElementById('main-cart-footer');
        if (wrapper) wrapper.classList.toggle('is-empty', empty);
        if (footer) footer.classList.toggle('is-empty', empty);
        list.forEach((s) => {
          const container = document.getElementById(s.id);
          const html = secs[s.section];
          if (!container || html == null) return;
          const target = container.querySelector(s.selector) || container;
          target.innerHTML = sectionInnerHTML(html, s.selector);
        });
      })
      .catch((e) => console.warn('[S01FreeGift]', e));
  }

  // Serialized + idempotent. Resolves { cart, changed, unlocked: [promo,...] }.
  function ensure() {
    const run = queue
      .then(getCart)
      .then((cart) => {
        const actions = [];
        const unlocked = [];

        ALL_PROMOS.forEach((promo) => {
          const unlock = promoUnlocked(cart, promo);
          const gift = giftLineFor(cart, promo);
          if (unlock && !gift) {
            actions.push(addGift(promo));
            unlocked.push(promo);
          } else if (unlock && gift && gift.quantity !== 1) {
            actions.push(setGiftQty(gift.key, 1));
          } else if (!unlock && gift) {
            actions.push(setGiftQty(gift.key, 0));
          }
        });

        if (!actions.length) {
          lastCart = cart;
          return { cart, changed: false, unlocked: [] };
        }
        return Promise.all(actions)
          .then(getCart)
          .then((newCart) => {
            lastCart = newCart;
            return refreshUI(newCart).then(() => ({ cart: newCart, changed: true, unlocked }));
          });
      })
      .catch((e) => {
        console.warn('[S01FreeGift]', e);
        return { cart: lastCart, changed: false, unlocked: [] };
      });

    // Chain the queue but never let a rejection deadlock it.
    queue = run.then(
      () => {},
      () => {}
    );
    return run;
  }

  // Public entry point. Resolves the settled cart; opens the celebration
  // modal (with fly-to-cart animation) for any promo(s) newly unlocked.
  function reconcile(track) {
    return ensure().then((res) => {
      if (track && res.unlocked && res.unlocked.length) openGiftModal(res.unlocked);
      return res.cart;
    });
  }

  /* Cart-page presentation (locking the gift line's quantity/remove controls and
     the "Free gift" tag) is rendered server-side in sections/main-cart-items.liquid,
     keyed off the line's `_free_gift` property. That is authoritative on every load
     and re-render, so no JS DOM decoration is needed here. */

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === SOURCE) return; // defensive: ignore our own events
        reconcile(true); // user-driven add/remove — may raise the fresh-item signal
      });
    }
    // Reconcile on load (direct cart-page visits, back button, etc.) WITHOUT
    // opening the modal — a page load isn't a "just unlocked" celebration moment.
    reconcile(false);
  }

  /* ---- Celebration modal + fly-to-cart animation --------------------------- */

  function cartIconEl() {
    return document.getElementById('s01CartLink') || document.querySelector('[data-cart-count]');
  }

  // Clones a small thumbnail of the gift and animates it from the modal's
  // panel toward the header cart icon, ending with a bump on the count bubble.
  function flyToCart(fromEl, imageUrl) {
    const to = cartIconEl();
    if (!fromEl || !to || !imageUrl) return;
    const fromRect = fromEl.getBoundingClientRect();
    const toRect = to.getBoundingClientRect();

    const ghost = document.createElement('img');
    ghost.src = imageUrl;
    ghost.alt = '';
    ghost.className = 's01-gift-fly';
    ghost.style.left = fromRect.left + fromRect.width / 2 - 18 + 'px';
    ghost.style.top = fromRect.top + fromRect.height / 2 - 18 + 'px';
    document.body.appendChild(ghost);

    const dx = toRect.left + toRect.width / 2 - (fromRect.left + fromRect.width / 2);
    const dy = toRect.top + toRect.height / 2 - (fromRect.top + fromRect.height / 2);
    ghost.style.setProperty('--s01-fly-dx', dx + 'px');
    ghost.style.setProperty('--s01-fly-dy', dy + 'px');

    requestAnimationFrame(() => ghost.classList.add('is-flying'));
    ghost.addEventListener(
      'animationend',
      () => {
        ghost.remove();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.classList.remove('s01-bump');
          void el.offsetWidth;
          el.classList.add('s01-bump');
        });
      },
      { once: true }
    );
  }

  function openGiftModal(promos) {
    const modal = document.getElementById('s01GiftModal');
    if (!modal || typeof modal.open !== 'function') return; // no modal markup — silently skip
    modal.open(promos, flyToCart);
  }

  // Expose for the cart toast so the add flow can await reconciliation before
  // it repaints its totals — the celebration itself lives entirely here,
  // via the <s01-gift-modal> opened from reconcile() above.
  cfg.ensure = () => reconcile(true);
  cfg.isGift = isGiftLine;
  cfg.promoList = PROMOS;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* ---- <s01-gift-modal> custom element -------------------------------------- */
  if (!customElements.get('s01-gift-modal')) {
    customElements.define(
      's01-gift-modal',
      class S01GiftModal extends HTMLElement {
        constructor() {
          super();
          this.panel = this.querySelector('.s01-gift-modal__panel');
          this.list = this.querySelector('[data-gift-modal-list]');
          this.titleEl = this.querySelector('[data-gift-modal-title]');
          this.subtitleEl = this.querySelector('[data-gift-modal-subtitle]');
          this.previouslyFocused = null;
          this.autoCloseTimer = null;

          this.querySelectorAll('[data-gift-modal-close]').forEach((el) =>
            el.addEventListener('click', () => this.close())
          );
          document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
          });
        }

        open(promos, flyFn) {
          if (!promos || !promos.length) return;
          clearTimeout(this.autoCloseTimer);

          this.titleEl.textContent =
            promos.length > 1 ? "You've unlocked free gifts!" : "You've unlocked a free gift!";
          this.subtitleEl.textContent = "It's on us — added to your cart automatically.";

          this.list.innerHTML = '';
          promos.forEach((promo) => {
            const item = document.createElement('div');
            item.className = 's01-gift-modal__item';
            item.innerHTML =
              '<span class="s01-gift-modal__item-thumb">' +
              (promo.image ? '<img src="' + promo.image + '" alt="">' : '') +
              '</span>' +
              '<div class="s01-gift-modal__item-meta">' +
              '<p class="s01-gift-modal__item-name"></p>' +
              '<p class="s01-gift-modal__item-note">Added to your cart</p>' +
              '</div>' +
              '<span class="s01-gift-modal__item-pill">FREE</span>';
            item.querySelector('.s01-gift-modal__item-name').textContent = promo.title || 'Free gift';
            this.list.appendChild(item);
          });

          this.previouslyFocused = document.activeElement;
          this.setAttribute('aria-hidden', 'false');
          this.classList.add('is-open');
          document.documentElement.classList.add('s01-modal-open');
          this.panel.focus({ preventScroll: true });

          if (typeof flyFn === 'function') {
            requestAnimationFrame(() => {
              this.list.querySelectorAll('.s01-gift-modal__item-thumb').forEach((thumb, i) => {
                const promo = promos[i];
                if (promo && promo.image) flyFn(thumb, promo.image);
              });
            });
          }

          this.autoCloseTimer = setTimeout(() => this.close(), 6000);
        }

        close() {
          clearTimeout(this.autoCloseTimer);
          this.classList.remove('is-open');
          this.setAttribute('aria-hidden', 'true');
          document.documentElement.classList.remove('s01-modal-open');
          if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
            this.previouslyFocused.focus();
          }
        }
      }
    );
  }
})();
